<!--
<template>
  <div class="opc-input-number">
    <div
      @dragstart.prevent
      :class="[
        'zbo-input-number',
        'opc-input-selectable',
        inputNumberSize ? 'zbo-input-number--' + inputNumberSize : '',
        { 'is-disabled': inputNumberDisabled },
        { 'is-without-controls': !controls },
        { 'is-controls-right': controlsAtRight },
      ]"
    >
      <zm-popover v-if="!!decreaseTips" key='popper__decreaseTips_tips_zm' :placement="placement" width="320" trigger="hover">
        <p v-html="decreaseTips"></p>
        <span
          class="zbo-input-number__decrease"
          role="button"
          key='popper__decrease'
          v-if="controls"
          data-testid="checkout-span-input-number-decrease"
          :aria-label="`${label} ${currentValue} Decrease`"
          :aria-valuenow="currentValue"
          v-repeat-click="decrease"
          :class="{ 'is-disabled': minDisabled || !!decreaseTips }"
          @keydown.enter="decrease"
          slot="reference"
        >
          <i :class="`zm-icon-${controlsAtRight ? 'down' : 'minus'}`"></i>
        </span>
      </zm-popover>
      <template v-else>
        <span
          class="zbo-input-number__decrease"
          role="button"
          key='span__descrease'
          v-if="controls"
          data-testid="checkout-span-input-number-decrease"
          :aria-label="`${label} ${currentValue} Decrease`"
          :aria-valuenow="currentValue"
          v-repeat-click="decrease"
          :class="{ 'is-disabled': minDisabled }"
          @keydown.enter="decrease"
        >
          <i :class="`zm-icon-${controlsAtRight ? 'down' : 'minus'}`"></i>
        </span>
      </template>
      <zm-popover v-if="!!increaseTips" key='popper__increaseTips_tips_zm' :placement="placement" width="320" trigger="hover">
        <p style="word-break: break-word" v-html="increaseTips"></p>
        <span
          class="zbo-input-number__increase"
          role="button"
          key='popper__increase'
          v-if="controls"
          data-testid="checkout-span-input-number-increase"
          :aria-label="`${label} ${currentValue} Increase`"
          :aria-valuenow="currentValue"
          v-repeat-click="increase"
          :class="{ 'is-disabled': maxDisabled || !!increaseTips }"
          slot="reference"
          @keydown.enter="increase"
        >
          <i :class="`zm-icon-${controlsAtRight ? 'up' : 'add'}`"></i>
        </span>
      </zm-popover>
      <template v-else>
        <span
          class="zbo-input-number__increase"
          role="button"
          key='span__increase'
          v-if="controls"
          data-testid="checkout-span-input-number-increase"
          :aria-label="`${label} ${currentValue} Increase`"
          :aria-valuenow="currentValue"
          v-repeat-click="increase"
          :class="{ 'is-disabled': maxDisabled }"
          @keydown.enter="increase"
        >
          <i :class="`zm-icon-${controlsAtRight ? 'up' : 'add'}`"></i>
        </span>
      </template>
      <zm-input
        :data-testid="dataTestid"
        ref="input"
        :type="!is_desktop_reactive ? 'number' : 'text'"
        :pattern="!is_desktop_reactive ? '[0-9]*' : null"
        :value="displayValue"
        :placeholder="placeholder"
        :disabled="inputNumberDisabled"
        :size="inputNumberSize"
        :max="max"
        :min="min"
        :name="name"
        :tabindex="tabindex"
        :label="disabledTips ? `${label} ${disabledTips}` : label"
        @keydown.up.native.prevent="increase"
        @keydown.down.native.prevent="decrease"
        @blur="handleBlur"
        @focus="handleFocus"
        @input="handleInput"
        @change="handleInputChange"
        data-1p-ignore
      />
    </div>
    <slot name="unit">
      <span v-if="unit" class="opc-input-number__unit">{{ unit }}</span>
    </slot>
    <slot name="tooltip">
      <zm-tooltip
        v-if="tooltip"
        class="opc-input-number__tooltip"
        :ariaLabel="tooltipLabel"
        :content="tooltip"
        placement="bottom"
      >
        <tips-icon type="info" :aria-label="tooltipLabel" />
      </zm-tooltip>
    </slot>
  </div>
</template>

<script>
import InputNumber from '@zoom/zoom-ui/src/components/input-number';
import string, { isNumber } from 'lodash';

export default {
  name: 'input-number-with-popover',
  extends: InputNumber,
  props: {
    dataTracking: {
      type: Object,
      required: false,
    },
    increaseTips: {
      type: String,
      default: '',
    },
    decreaseTips: {
      type: String,
      default: '',
    },
    placement: {
      type: String,
      default: 'bottom',
    },
    precision: {
      type: Number,
      default: 0,
      validator(val) {
        return val >= 0 && val === parseInt(val, 10);
      },
    },
    unit: String,
    tooltip: String,
    tabindex: {
      type: Number,
      default: 0,
    },
    dataTestid: {
      type: String,
      default: 'checkout-input-28',
    },
  },
  methods: {
    handleInputChange(value) {
      if (value === '') value = this.currentValue;
      const newVal = value === '' ? undefined : Number(value);
      if (!isNaN(newVal) || value === '') {
        this.setCurrentValue(newVal);
      }
      this.userInput = null;
    },
  },
  computed: {
    disabledTips() {
      return this.increaseTips || this.decreaseTips;
    },
  },
  watch: {
    currentValue(newVal, oldVal) {
      if (this.dataTracking) {
        if (isNumber(newVal) && isNumber(oldVal)) {
          let count = newVal - oldVal;
          if (count === 0) {
            return;
          }
          let action = 'increase';
          if (count < 0) {
            action = 'decrease';
          }
          let plan = string.split(this.label, 'Select')[0] || '';
          const { buyInteraction = '', buySelection = '', planName = plan } = this.dataTracking
          this.$tracking.buyInteraction(
            buyInteraction,
            buySelection ? `${buySelection} ${planName} - ${action} - ${newVal}` : `${planName} - ${action} - ${newVal}`,
          );
        }
      }
    },
  },
};
</script>
<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';
.opc-input-number {
  position: relative;
  display: inline-block;

  &__unit {
    margin-left: 8px;
    display: inline-block;

    &::first-letter {
      text-transform: uppercase;
    }
  }
  @include media(mobile, tablet) {
    .zbo-input__inner[type='number'] {
      padding-right: 47px;

      -moz-appearance: textfield; /* Firefox */

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }
}

input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield; /* Firefox */
}
</style>
-->

<template>
  <Teleport v-bind="teleportProps">
    <div class="opc-input-number" v-bind="$attrs">
      <span class="opc-input-number__inner">
        <zm-input-number ref="input-number" v-bind="$props" :data-testid="dataTestid" :modelValue="val" @update:modelValue="onChange"
          class="opc-input-number__core" size="lg" :min="minVal" :placeholder="String(minVal)" :patterns="/[^0-9]/g" :extra-attributes="extraAttributes">
          <template #decrease-icon>
            <zm-popover :placement="placement" trigger="hover" :disabled="!decreaseTips" teleported floating-content-class="opc-input-number__tooltip__content" :z-index="999">
              <zm-icon>
                <minus />
              </zm-icon>
              <template #content>
                <p v-html="decreaseTips"></p>
              </template>
            </zm-popover>
          </template>
          <template #increase-icon>
            <zm-popover :placement="placement" trigger="hover" :disabled="!increaseTips" teleported floating-content-class="opc-input-number__tooltip__content" :z-index="999">
              <zm-icon>
                <plus />
              </zm-icon>
              <template #content>
                <p v-html="increaseTips"></p>
              </template>
            </zm-popover>
          </template>
        </zm-input-number>
      </span>
      <slot name="unit">
        <span v-if="unit" class="opc-input-number__unit">{{ unit }}</span>
      </slot>
      <slot name="tooltip">
        <zm-tooltip v-if="tooltip" class="opc-input-number__tooltip" :ariaLabel="tooltipLabel" :placement="placement">
          <zm-icon>
            <information />
          </zm-icon>
          <template #content>
            <span v-html="tooltip"></span>
          </template>
        </zm-tooltip>
      </slot>
    </div>
  </Teleport>
</template>

<script>
import { ZmInputNumber } from '@zoom/zoom-ui-vue3';
import { Plus, Minus, InformationSmall as Information } from '@zoom/icons-vue';
import { Teleport } from 'vue';
import string, { isNumber } from 'lodash';
import { ONE_PASS_LIKE_IGNORE_ATTRS } from '@/common/constants';
export default {
  extends: ZmInputNumber,
  inheritAttrs: false,
  props: {
    value: {
      type: String,
      default: ""
    },
    dataTracking: {
      type: Object,
      required: false,
    },
    increaseTips: {
      type: String,
      default: '',
    },
    decreaseTips: {
      type: String,
      default: '',
    },
    placement: {
      type: String,
      default: 'bottom',
    },
    precision: {
      type: Number,
      default: 0,
      validator(val) {
        return val >= 0 && val === parseInt(val, 10);
      },
    },
    unit: {
      type: String,
      default: ""
    },
    tooltip: {
      type: String,
      default: ""
    },
    // new add: target for teleport
    teleportTarget: {
      type: String,
      default: ''
    },
    dataTestid: {
      type: String,
      default: '',
    },
  },
  emits: ['change'],
  components: {
    Plus,
    Minus,
    Information,
    Teleport
  },
  computed: {
    val(){
      return this.modelValue == undefined ? this.value : this.modelValue;
    },
    minVal(){
      return this.min !== undefined ? this.min : 1;
    },
    teleportProps() {
      return {
        to: this.teleportTarget || 'body',
        disabled: !this.teleportTarget
      };
    },
    extraAttributes(){
      return {
        ...ONE_PASS_LIKE_IGNORE_ATTRS.reduce((acc, attr) => (acc[attr] = 'true', acc), {})
      };
    }
  },
  methods: {
    onChange(value){
      if(value === null || value === undefined || value < this.minVal) {
        this.$emit("change", this.minVal)
        this.$emit("update:modelValue", this.minVal)
        return;
      };
      this.$emit("change", value)
      this.$emit("update:modelValue", value)
    },
  },
  watch: {
    val(newVal, oldVal){
      if (this.dataTracking) {
        if (isNumber(newVal) && isNumber(oldVal)) {
          let count = newVal - oldVal;
          if (count === 0) {
            return;
          }
          let action = 'increase';
          if (count < 0) {
            action = 'decrease';
          }
          let plan = string.split(this.label, 'Select')[0] || '';
          const { buyInteraction = '', buySelection = '', planName = plan } = this.dataTracking
          this.$tracking.buyInteraction(
            buyInteraction,
            buySelection ? `${buySelection} ${planName} - ${action} - ${newVal}` : `${planName} - ${action} - ${newVal}`,
          );
        }
      }
    }
  }
}
</script>

<style lang="scss">
.opc-input-number__tooltip__content{
  min-width: 300px !important;
}
</style>

<style lang="scss" scoped>

$size: 26px;
$border: 1px solid #C1C6CE;

.opc-input-number{
  vertical-align: middle;
  position: relative;
  display: inline-block;
  &__unit {
    margin-left: 8px;
    display: inline-block;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 16px;
  }
  &__inner{
    vertical-align: middle;
    position: relative;
  }
  &__tooltip{
    vertical-align: middle;
  }
  :deep{
    .zbo-popover {
      z-index: 0;
    }
  }
  &__core{
    width: 160px;
    :deep{
      &.zbo-input-number{
        border: $border;
        border-radius: 8px;
        // overflow: hidden;
      }
      .zbo-input-number__input{
        text-align: center;
        padding-left: 11px !important;
        padding-right: 11px !important;
        font-size: 18px !important;
        border: none;
      }
      .zbo-input-number__decrease{
        user-select: none;
        top: 50% !important;
        transform: translateY(-50%);
        left: 0px !important;
        // border-right: none !important;
        height: 100% !important;
        padding-left: 20px;
        padding-right: 20px;
        border-right: $border;
        border-radius: 8px 0 0 8px;
        &.is-disabled{
          background: var(--zoom-color-state-subtle-disable, rgba(173, 177, 184, 0.25));
        }
      }
      .zbo-input-number__increase{
        user-select: none;
        top: 50% !important;
        transform: translateY(-50%);
        right: 0px !important;
        // border-left: none !important;
        height: 100% !important;
        padding-left: 20px;
        padding-right: 20px;
        border-left: $border;
        border-radius: 0 8px 8px 0;
        &.is-disabled{
          background: var(--zoom-color-state-subtle-disable, rgba(173, 177, 184, 0.25));
        }
      }
      .zbo-icon{
        position: absolute;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }
  }
  :deep{
    .zbo-input-number--lg .zbo-input-number__input {
      padding-top: 8px !important;
      padding-bottom: 7px !important;
    }
    .zbo-input-number--md .zbo-input-number__input {
      padding-top: 3px !important;
      padding-bottom: 3px !important;
    }
    .zbo-input-number--sm .zbo-input-number__input {
      padding-top: 0px !important;
      padding-bottom: 0px !important;
    }
  }
}
</style>

<style lang="scss">
@import "@/styles/soft-bundle/input-number-embed.scss";
</style>
